import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "../dataManagement/uiDb/UiDb";
import { setCurrentBusinessGallery } from "../dataManagement/uiDb/BusinessDb";
import BusinessApiService from "../services/apiServices/BusinessApiService";
import { useMediaUpload } from "./useMediaUpload";
import { MediaRecord, MediaUploadBatch, PendingUpload } from "../interfaces/MediaObject";
import mediaUtility from "../services/utility/MediaUtility";

export interface UseBusinessGalleryReturn {
  /** Permanent publicUrl-backed media records (images / videos / PDFs). */
  media: MediaRecord[];
  /** In-flight / failed uploads, shown as optimistic tiles in the grid. */
  pending: PendingUpload[];
  isLoading: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  /**
   * Uploads files (optionally into a section) via the presigned-storage flow,
   * persists them, and appends to local state. Shows optimistic "uploading"
   * tiles while in flight. Resolves to a per-file batch result. Never throws.
   */
  uploadMedia: (files: File[], section?: string) => Promise<MediaUploadBatch>;
  /** Retries a single failed pending upload. */
  retryPending: (id: string) => void;
  /** Removes a failed pending tile without retrying. */
  dismissPending: (id: string) => void;
  /** Optimistically removes the item by object path; rolls back on failure. */
  deleteMedia: (objectPath: string) => Promise<void>;
  /** Renames a section across every item currently filed under it. */
  renameSection: (oldName: string, newName: string) => Promise<void>;
  /** Re-pulls the gallery from the backend. */
  refresh: () => Promise<void>;
}

function makeId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Mirrors GalleryTab's own DEFAULT_SECTION (and GalleryComposer's) -- each file
// in this feature keeps its own copy of this literal rather than a shared
// export, matching the existing convention here.
const DEFAULT_GALLERY_SECTION = "General";

export function useBusinessGallery(businessId: string): UseBusinessGalleryReturn {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const storedMedia = useSelector((state: RootState) => state.business.currentBusinessGallery);
  const media = storedMedia ?? [];
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  // Tracks which businessId we've already fetched, so a businessId change
  // refetches (and strict-mode remounts for the same id don't double-fetch).
  const fetchedForRef = useRef<string | null>(null);

  // Mirror pending into a ref so the unmount cleanup can revoke object URLs.
  const pendingRef = useRef<PendingUpload[]>([]);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  useEffect(
    () => () => {
      pendingRef.current.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    },
    []
  );

  const refresh = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    try {
      const records = await BusinessApiService.getBusinessMedia(businessId);
      dispatch(setCurrentBusinessGallery(records));
    } catch {
      dispatch(setCurrentBusinessGallery([]));
    } finally {
      setIsLoading(false);
    }
  }, [businessId, dispatch]);

  // Fetch on mount and whenever the businessId changes.
  useEffect(() => {
    if (!businessId || fetchedForRef.current === businessId) return;
    fetchedForRef.current = businessId;
    // Read the live store, not the render-time `storedMedia`: useBusinessListings clears it first.
    if (store.getState().business.currentBusinessGallery === null) {
      refresh();
    } else {
      setIsLoading(false);
    }
  }, [businessId, refresh, storedMedia, store]);

  // Persist step handed to the generic upload engine. The backend has returned
  // 200s that didn't actually persist, so after the PUT we verify against a
  // fresh server read instead of optimistically appending — the grid then
  // always mirrors the DB, never a phantom blob.
  const persist = useCallback(
    async (records: MediaRecord[]) => {
      await BusinessApiService.appendBusinessMedia(businessId, records);

      // Read-back verification. If the verification GET itself fails (network
      // blip), fall back to the optimistic append — the PUT already passed its
      // status + envelope checks.
      let server: MediaRecord[];
      try {
        server = await BusinessApiService.getBusinessMedia(businessId);
      } catch {
        dispatch(setCurrentBusinessGallery([...media, ...records]));
        return;
      }

      const stored = new Set(server.map((m) => m.objectPath));
      dispatch(setCurrentBusinessGallery(server));
      if (records.some((r) => !stored.has(r.objectPath))) {
        throw new Error("Uploaded, but couldn't save to the gallery. Please try again.");
      }

      // Hand the canonical server records back to the upload engine so
      // `succeeded` reflects server-normalized fields (e.g. section).
      return records.map(
        (r) => server.find((m) => m.objectPath === r.objectPath) ?? r
      );
    },
    [businessId]
  );

  const { isUploading, upload } = useMediaUpload({ folder: "business", persist });

  const uploadMedia = useCallback(
    async (files: File[], section?: string): Promise<MediaUploadBatch> => {
      // 1. Create optimistic pending tiles up front (with local previews).
      const newPending: PendingUpload[] = files.map((file) => {
        const contentType = mediaUtility.resolveContentType(file);
        const kind = contentType ? mediaUtility.kindFromContentType(contentType) : "image";
        const previewUrl =
          contentType && (kind === "image" || kind === "video")
            ? URL.createObjectURL(file)
            : undefined;
        return { id: makeId(), file, kind, previewUrl, section, status: "uploading" };
      });
      setPending((prev) => [...prev, ...newPending]);

      // 2. Run the upload. Results are in the same order as `files`/`newPending`.
      const batch = await upload(files, section);

      // 3. Reconcile: drop succeeded tiles (real media now rendered), keep failed
      //    ones in an error state for retry/dismiss.
      const verdicts = new Map<string, { ok: boolean; message?: string }>();
      newPending.forEach((p, i) => {
        const r = batch.results[i];
        verdicts.set(p.id, {
          ok: !r || r.status === "success",
          message: r && r.status === "error" ? r.message : undefined,
        });
      });
      setPending((prev) =>
        prev.flatMap((p) => {
          const v = verdicts.get(p.id);
          if (!v) return [p];
          if (v.ok) {
            if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
            return [];
          }
          return [{ ...p, status: "error" as const, message: v.message }];
        })
      );

      return batch;
    },
    [upload]
  );

  const dismissPending = useCallback((id: string) => {
    setPending((prev) =>
      prev.filter((p) => {
        if (p.id !== id) return true;
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        return false;
      })
    );
  }, []);

  const retryPending = useCallback(
    (id: string) => {
      const target = pendingRef.current.find((p) => p.id === id);
      if (!target) return;
      dismissPending(id);
      void uploadMedia([target.file], target.section);
    },
    [dismissPending, uploadMedia]
  );

  const deleteMedia = useCallback(
    async (objectPath: string) => {
      // Capture the pre-delete list via a functional update so rapid, concurrent
      // deletes each roll back against the live state (not a stale render snapshot).
      let snapshot: MediaRecord[] = [];
      snapshot = media;
      dispatch(setCurrentBusinessGallery(media.filter((m) => m.objectPath !== objectPath)));
      setIsDeleting(true);
      try {
        await BusinessApiService.deleteBusinessMedia(businessId, objectPath);
      } catch (err) {
        // Rollback on failure
        dispatch(setCurrentBusinessGallery(snapshot));
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [businessId, media, dispatch]
  );

  const renameSection = useCallback(
    async (oldName: string, newName: string) => {
      const snapshot = media;
      // Optimistic re-point: match the same way GalleryTab groups items (an
      // unset/blank section falls into the default "General" bucket), so
      // renaming that bucket also re-points items with no section set.
      const matches = (m: MediaRecord) => (m.section?.trim() || DEFAULT_GALLERY_SECTION) === oldName;
      dispatch(
        setCurrentBusinessGallery(
          media.map((m) => (matches(m) ? { ...m, section: newName } : m))
        )
      );
      try {
        await BusinessApiService.renameGallerySection(businessId, oldName, newName);
      } catch (err) {
        // Rollback on failure
        dispatch(setCurrentBusinessGallery(snapshot));
        throw err;
      }
    },
    [businessId, media, dispatch]
  );

  return {
    media,
    pending,
    isLoading,
    isUploading,
    isDeleting,
    uploadMedia,
    retryPending,
    dismissPending,
    deleteMedia,
    renameSection,
    refresh,
  };
}
